import { useCallback, useRef } from "react";

import { MIN_WINDOW_SIZE } from "../constants";

interface UseWindowInteractionsOptions {
  sessionId: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  updateWindowPosition: (id: string, position: { x: number; y: number }) => void;
  updateWindowSize: (id: string, size: { width: number; height: number }) => void;
}

export function useWindowInteractions({
  sessionId,
  position,
  size,
  updateWindowPosition,
  updateWindowSize,
}: UseWindowInteractionsOptions) {
  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0 });

  const handleDragStart = useCallback(
    (event: React.PointerEvent) => {
      isDragging.current = true;
      dragOffset.current = {
        x: event.clientX - position.x,
        y: event.clientY - position.y,
      };
      (event.target as HTMLElement).setPointerCapture(event.pointerId);
    },
    [position]
  );

  const handleDragMove = useCallback(
    (event: React.PointerEvent) => {
      if (!isDragging.current) {
        return;
      }

      updateWindowPosition(sessionId, {
        x: event.clientX - dragOffset.current.x,
        y: event.clientY - dragOffset.current.y,
      });
    },
    [sessionId, updateWindowPosition]
  );

  const handleDragEnd = useCallback(() => {
    isDragging.current = false;
  }, []);

  const handleResizeStart = useCallback(
    (event: React.PointerEvent) => {
      event.stopPropagation();
      isResizing.current = true;
      resizeStart.current = {
        x: event.clientX,
        y: event.clientY,
        width: size.width,
        height: size.height,
      };
      (event.target as HTMLElement).setPointerCapture(event.pointerId);
    },
    [size]
  );

  const handleResizeMove = useCallback(
    (event: React.PointerEvent) => {
      if (!isResizing.current) {
        return;
      }

      const deltaX = event.clientX - resizeStart.current.x;
      const deltaY = event.clientY - resizeStart.current.y;
      updateWindowSize(sessionId, {
        width: Math.max(MIN_WINDOW_SIZE.width, resizeStart.current.width + deltaX),
        height: Math.max(MIN_WINDOW_SIZE.height, resizeStart.current.height + deltaY),
      });
    },
    [sessionId, updateWindowSize]
  );

  const handleResizeEnd = useCallback(() => {
    isResizing.current = false;
  }, []);

  return {
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleResizeStart,
    handleResizeMove,
    handleResizeEnd,
  };
}
